import cx from 'classnames'
import Button from 'components/Button'
import PageTip from 'components/PageTip'
import * as React from 'react'
import styles from './ContentLayout.sass'

interface ContentProps {
  children?: React.ReactNode
  /**
   * Fills the remaining height in the parent, if the parent also does this.
   */
  fill?: boolean
  /**
   * Use this if the header renders a tab bar.
   */
  accountForTabBar?: boolean
  /**
   * Use this if the content renders a section-like label as the first element.
   */
  accountForLabel?: boolean
  /**
   * Removes all padding from root styling.
   */
  noPadding?: boolean
}

export const ContentLayout = React.forwardRef(
  (
    {
      children,
      accountForTabBar,
      accountForLabel,
      fill,
      noPadding,
    }: ContentProps,
    ref: React.Ref<HTMLDivElement>
  ) => (
    <div
      ref={ref}
      className={cx(
        styles.root,
        accountForTabBar && styles.accountForTabBar,
        accountForLabel && styles.accountForLabel,
        fill && styles.fill,
        noPadding && styles.noPadding
      )}
    >
      {children}
    </div>
  )
)

interface ContentMainProps {
  children?: React.ReactNode
  /**
   * Fill the available space in the parent.
   */
  fill?: boolean
  /**
   * Constrain the width to not be more than the parent's width.
   */
  constrainWidth?: boolean
}

export function ContentMain({
  children,
  fill,
  constrainWidth,
}: ContentMainProps) {
  return (
    <div
      className={cx(
        styles.main,
        fill && styles.fill,
        constrainWidth && styles.constrainWidth
      )}
    >
      {children}
    </div>
  )
}

interface ContentTipProps {
  title: string
  children?: React.ReactNode
  helpCenterUrl?: string
  illustrationUrl: string
}

export function ContentTip({
  children,
  title,
  illustrationUrl,
  helpCenterUrl,
}: ContentTipProps) {
  return (
    <div className={cx(styles.tip)}>
      <PageTip
        title={title}
        illustrationUrl={illustrationUrl}
        button={
          helpCenterUrl && (
            <Button type="primary" href={helpCenterUrl} target="_blank">
              Visit our Help Center
            </Button>
          )
        }
      >
        {children}
      </PageTip>
    </div>
  )
}

interface ContentEmptyProps {
  children?: React.ReactNode
}

export function ContentEmpty({ children }: ContentEmptyProps) {
  return <div className={styles.empty}>{children}</div>
}
